import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  TextField,
  InputAdornment,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  TrendingUp as AnalyticsIcon,
  ShoppingCart as ReservationsIcon,
  AttachMoney as RevenueIcon,
  Percent as ConversionIcon,
  LocalOffer as PriceIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  PointOfSale as POSIcon,
  BarChart as ChartIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  MonetizationOn as PricingIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import CampaignTimeline from '../../components/CampaignTimeline';

const CampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTier, setExpandedTier] = useState(null);

  // POS Sale form state
  const [posSaleData, setPosSaleData] = useState({});
  const [pricingData, setPricingData] = useState({});

  // Fetch campaign data
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch actual campaign data from API
        const response = await fetch(`/api/campaigns/${id}`);

        if (!response.ok) {
          throw new Error(`Failed to load campaign: ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.success || !data.campaign) {
          throw new Error('Invalid campaign data');
        }

        setCampaign(data.campaign);
        setLoading(false);
      } catch (err) {
        console.error('Error loading campaign:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (id) {
      fetchCampaign();
    }
  }, [id]);

  // If you want to keep mock data as fallback, use this instead:
  /*
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to fetch real data first
        try {
          const response = await fetch(`/api/campaigns/${id}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.campaign) {
              setCampaign(data.campaign);
              setLoading(false);
              return;
            }
          }
        } catch (apiError) {
          console.warn('API fetch failed, using mock data:', apiError);
        }

        // Fallback to mock campaign data
        const mockCampaign = {
          _id: id,
          name: 'Summer Collection Drop',
          slug: 'summer-collection-drop',
          status: 'active',
          description: 'Limited edition summer collection featuring premium sustainable fabrics and modern silhouettes.',
          timeline: {
            createdAt: new Date('2025-01-15'),
            launchDate: new Date('2025-02-01'),
            endDate: new Date('2025-03-31'),
            reservationPeriod: {
              startDate: new Date('2025-02-01'),
              endDate: new Date('2025-02-15')
            },
            productionPeriod: {
              startDate: new Date('2025-02-16'),
              endDate: new Date('2025-03-10'),
              leadTimeDays: 21
            },
            delivery: {
              estimatedDeliveryDate: new Date('2025-03-31'),
              shippingDays: 7
            }
          },
          analytics: {
            totalReservations: 145,
            totalRevenue: 14500,
            conversionRate: 12.5,
            averageOrderValue: 100
          },
          tiers: [
            {
              name: 'Express',
              status: 'active',
              price: 129,
              currentReservations: 40,
              reservationTarget: 30,
              progress: 133,
              margin: 0.65,
              waitDays: 0,
              reservationPeriod: {
                startDate: new Date('2025-02-01'),
                endDate: new Date('2025-02-05')
              }
            },
            {
              name: 'Standard',
              status: 'active',
              price: 99,
              currentReservations: 55,
              reservationTarget: 60,
              progress: 92,
              margin: 0.58,
              waitDays: 7,
              reservationPeriod: {
                startDate: new Date('2025-02-05'),
                endDate: new Date('2025-02-10')
              }
            },
            {
              name: 'Early Bird',
              status: 'active',
              price: 79,
              currentReservations: 35,
              reservationTarget: 70,
              progress: 50,
              margin: 0.52,
              waitDays: 14,
              reservationPeriod: {
                startDate: new Date('2025-02-10'),
                endDate: new Date('2025-02-15')
              }
            },
            {
              name: 'Pre-Order',
              status: 'upcoming',
              price: 69,
              currentReservations: 15,
              reservationTarget: 90,
              progress: 17,
              margin: 0.48,
              waitDays: 21,
              reservationPeriod: {
                startDate: new Date('2025-02-15'),
                endDate: new Date('2025-02-20')
              }
            }
          ]
        };

        setCampaign(mockCampaign);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Failed to load campaign details. Please try again.');
        setLoading(false);
        toast.error('Failed to load campaign');
      }
    };

    fetchCampaign();
  }, [id]);
  */

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'draft':
        return 'default';
      case 'paused':
        return 'warning';
      case 'ended':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Expansion handlers
  const handleTierExpand = (tierIndex) => {
    setExpandedTier(expandedTier === tierIndex ? null : tierIndex);
  };

  // POS Flash Sale handlers
  const handlePOSSaleChange = (tierIndex, field, value) => {
    setPosSaleData(prev => ({
      ...prev,
      [tierIndex]: {
        ...prev[tierIndex],
        [field]: value
      }
    }));
  };

  const handlePOSSaleSubmit = (tierIndex) => {
    const tier = campaign.tiers[tierIndex];
    const saleData = posSaleData[tierIndex] || {};

    // Validation
    if (!saleData.discountPercent || saleData.discountPercent <= 0 || saleData.discountPercent > 100) {
      toast.error('Please enter a valid discount percentage (1-100)');
      return;
    }

    if (!saleData.duration || saleData.duration <= 0) {
      toast.error('Please enter a valid duration');
      return;
    }

    const discountedPrice = tier.price * (1 - saleData.discountPercent / 100);
    const durationText = saleData.durationType === 'hours'
      ? `${saleData.duration} hour${saleData.duration > 1 ? 's' : ''}`
      : `${saleData.duration} day${saleData.duration > 1 ? 's' : ''}`;

    toast.success(
      `Flash sale activated for ${tier.name} tier! ${saleData.discountPercent}% off (${formatCurrency(tier.price)} → ${formatCurrency(discountedPrice)}) for ${durationText}`
    );
    // TODO: API call to activate flash sale for this tier via sivad-storefront
    // - Update tier with flash sale badge/timer
    // - Send notifications to subscribers

    // Reset form
    setPosSaleData(prev => ({
      ...prev,
      [tierIndex]: {}
    }));
  };

  // Pricing adjustment handlers
  const handlePricingChange = (tierIndex, value) => {
    setPricingData(prev => ({
      ...prev,
      [tierIndex]: value
    }));
  };

  const handlePricingSubmit = (tierIndex) => {
    const tier = campaign.tiers[tierIndex];
    const newPrice = pricingData[tierIndex];

    if (!newPrice || newPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    // Confirmation dialog
    const priceChange = newPrice - tier.price;
    const changeType = priceChange < 0 ? 'decrease' : 'increase';
    const changeAmount = Math.abs(priceChange);

    const confirmed = window.confirm(
      `⚠️ PERMANENT MARKDOWN CONFIRMATION\n\n` +
      `This will permanently ${changeType} the ${tier.name} tier price:\n\n` +
      `From: ${formatCurrency(tier.price)}\n` +
      `To: ${formatCurrency(newPrice)}\n` +
      `Change: ${priceChange < 0 ? '-' : '+'}${formatCurrency(changeAmount)}\n\n` +
      `This action cannot be undone.\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) {
      return;
    }

    toast.success(`Updated ${tier.name} tier price from ${formatCurrency(tier.price)} to ${formatCurrency(newPrice)}`);
    // TODO: API call to update pricing

    // Reset form
    setPricingData(prev => ({
      ...prev,
      [tierIndex]: ''
    }));
  };

  // Tier status handlers
  const handleToggleTierStatus = (tierIndex) => {
    const tier = campaign.tiers[tierIndex];
    if (tier.status === 'active') {
      toast.success(`Paused ${tier.name} tier`);
      // TODO: API call to pause tier
    } else if (tier.status === 'paused') {
      toast.success(`Resumed ${tier.name} tier`);
      // TODO: API call to resume tier
    }
  };

  const handleViewAnalytics = (tierIndex) => {
    const tier = campaign.tiers[tierIndex];
    toast.success(`Opening detailed analytics for ${tier.name} tier`);
    // TODO: Navigate to tier analytics page or open modal
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !campaign) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error || 'Campaign not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/campaigns')} sx={{ mt: 2 }}>
          Back to Campaigns
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/campaigns')} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            {campaign.name}
          </Typography>
          <Chip
            label={campaign.status}
            color={getStatusColor(campaign.status)}
            sx={{ textTransform: 'capitalize', mr: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/campaigns/${id}/edit`)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            onClick={() => navigate(`/campaigns/${id}/analytics`)}
          >
            Advanced Analytics
          </Button>
        </Box>

        {campaign.description && (
          <Typography variant="body1" color="text.secondary">
            {campaign.description}
          </Typography>
        )}
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(8, 210, 255, 0.1)', border: '1px solid rgba(8, 210, 255, 0.3)' }}>
            <CardContent>
              <Box sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Reservations
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ alignItems: 'center', mb: 1, color: '#08D2FF' }}>
                {campaign.analytics?.totalReservations || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary"sx={{ alignItems: 'center', mb: 1 }}>
                Across all tiers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
            <CardContent>
              <Box sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ alignItems: 'center', mb: 1, color: '#4CAF50' }}>
                {formatCurrency(campaign.analytics?.totalRevenue || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary"sx={{ alignItems: 'center', mb: 1 }}>
                Projected: {formatCurrency((campaign.analytics?.totalRevenue || 0) * 1.3)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <CardContent>
              <Box sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Conversion Rate
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ alignItems: 'center', mb: 1, color: '#FF9800' }}>
                {(campaign.analytics?.conversionRate || 0).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary"sx={{ alignItems: 'center', mb: 1 }}>
                Industry avg: 8-10%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)', border: '1px solid rgba(156, 39, 176, 0.3)' }}>
            <CardContent>
              <Box sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Avg Order Value
                </Typography>

              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ alignItems: 'center', mb: 1, color: '#9C27B0' }}>
                {formatCurrency(campaign.analytics?.averageOrderValue || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary"sx={{ alignItems: 'center', mb: 1 }}>
                Per customer
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaign Timeline */}
      <Box sx={{ mb: 4 }}>
        <CampaignTimeline campaign={campaign} variant="detailed" />
      </Box>

      {/* Tier Performance */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>Tier Performance</Typography>
        <TableContainer>
          <Table>
            <TableHead> 
              <TableRow > 
                <TableCell width="50px"/> 
                <TableCell>Tier Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Progress</TableCell>
                <TableCell align="right">Reservations</TableCell>
                <TableCell align="right">Target</TableCell>
                <TableCell align="right">Margin</TableCell>
                <TableCell align="right">Wait Time</TableCell>
                <TableCell align="right">Period</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.tiers?.map((tier, index) => (
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: expandedTier === index ? 'rgba(255, 255, 255, 0.99)' : 'transparent'
                    }}
                    onClick={() => handleTierExpand(index)}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        sx={{
                          transform: expandedTier === index ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {tier.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tier.status}
                        color={getStatusColor(tier.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(tier.price)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 100 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(tier.progress, 100)}
                            sx={{
                              height: 8,
                              borderRadius: '6px',
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: tier.progress >= 100 ? '#4CAF50' : tier.progress >= 75 ? '#FF9800' : '#08D2FF'
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" fontWeight="medium">
                          {tier.progress}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {tier.currentReservations}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {tier.reservationTarget}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={tier.margin >= 0.55 ? 'success.main' : tier.margin >= 0.45 ? 'warning.main' : 'error.main'}>
                        {(tier.margin * 100).toFixed(0)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={tier.waitDays === 0 ? 'Ships immediately' : `${tier.waitDays} days after production starts`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {tier.waitDays === 0 ? 'Now' : `${tier.waitDays}d`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(tier.reservationPeriod.startDate)} - {formatDate(tier.reservationPeriod.endDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Tools Section */}
                  <TableRow>
                    <TableCell colSpan={10} sx={{ p: 0, borderBottom: expandedTier === index ? undefined : 'none' }}>
                      <Collapse in={expandedTier === index} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, bgcolor: 'rgba(8, 210, 255, 0.03)', borderTop: '1px solid rgba(8, 210, 255, 0.2)' }}>
                          <Grid container spacing={3}>
                            {/* Flash Sale Tool */}
                            <Grid item xs={12} md={6} lg={4}>
                              <Paper sx={{ p: 2, bgcolor: 'rgba(8, 210, 255, 0.05)', border: '1px solid rgba(8, 210, 255, 0.2)', borderRadius: '6px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    Flash Sale
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                  Create urgency with time-limited discounts to boost tier reservations
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Discount Percentage"
                                    type="number"
                                    value={posSaleData[index]?.discountPercent || ''}
                                    onChange={(e) => handlePOSSaleChange(index, 'discountPercent', e.target.value)}
                                    InputProps={{
                                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                      inputProps: { min: 1, max: 100 }
                                    }}
                                    helperText={
                                      posSaleData[index]?.discountPercent
                                        ? `New price: ${formatCurrency(tier.price * (1 - posSaleData[index].discountPercent / 100))}`
                                        : `Current price: ${formatCurrency(tier.price)}`
                                    }
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Duration"
                                      type="number"
                                      value={posSaleData[index]?.duration || ''}
                                      onChange={(e) => handlePOSSaleChange(index, 'duration', e.target.value)}
                                      InputProps={{
                                        inputProps: { min: 1 }
                                      }}
                                    />
                                    <TextField
                                      select
                                      size="small"
                                      label="Unit"
                                      value={posSaleData[index]?.durationType || 'hours'}
                                      onChange={(e) => handlePOSSaleChange(index, 'durationType', e.target.value)}
                                      sx={{ minWidth: 100 }}
                                    >
                                      <MenuItem value="hours">Hours</MenuItem>
                                      <MenuItem value="days">Days</MenuItem>
                                    </TextField>
                                  </Box>
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePOSSaleSubmit(index);
                                    }}
                                    sx={{
                                      bgcolor: '#08D2FF',
                                      '&:hover': { bgcolor: '#07B8E0' },
                                      borderRadius: '6px',
                                      mt: 'auto'
                                    }}
                                  >
                                    Activate Flash Sale
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>

                            {/* Permanent Markdown Tool */}
                            <Grid item xs={12} md={6} lg={4}>
                              <Paper sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', border: '1px solid rgba(156, 39, 176, 0.2)', borderRadius: '6px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    Permanent Markdown
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                  Permanently adjust tier base price (non-reversible)
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Current Price: {formatCurrency(tier.price)}
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="New Price"
                                    type="number"
                                    value={pricingData[index] || ''}
                                    onChange={(e) => handlePricingChange(index, e.target.value)}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                      inputProps: { min: 0, step: 0.01 }
                                    }}
                                  />
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePricingSubmit(index);
                                    }}
                                    sx={{
                                      bgcolor: '#9C27B0',
                                      '&:hover': { bgcolor: '#7B1FA2' },
                                      borderRadius: '6px',
                                      mt: 'auto'
                                    }}
                                  >
                                    Apply Markdown
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>

                            {/* Tier Actions Tool */}
                            <Grid item xs={12} md={6} lg={4}>
                              <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '6px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    Tier Actions
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleTierStatus(index);
                                    }}
                                    sx={{
                                      borderColor: tier.status === 'active' ? '#FF9800' : '#4CAF50',
                                      color: tier.status === 'active' ? '#FF9800' : '#4CAF50',
                                      borderRadius: '6px',
                                      '&:hover': {
                                        borderColor: tier.status === 'active' ? '#F57C00' : '#388E3C',
                                        bgcolor: tier.status === 'active' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)'
                                      }
                                    }}
                                  >
                                    {tier.status === 'active' ? 'Pause Tier' : 'Resume Tier'}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewAnalytics(index);
                                    }}
                                    sx={{
                                      borderColor: '#08D2FF',
                                      color: '#08D2FF',
                                      borderRadius: '6px',
                                      '&:hover': {
                                        borderColor: '#07B8E0',
                                        bgcolor: 'rgba(8, 210, 255, 0.1)'
                                      }
                                    }}
                                  >
                                    View Analytics
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Additional Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Campaign Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flexDirection: 'row', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Campaign ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {campaign._id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Slug
                </Typography>
                <Typography variant="body2">
                  {campaign.slug}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created Date 
                </Typography>
                <Typography variant="body2">
                  {formatDate(campaign.timeline.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Launch Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(campaign.timeline.launchDate)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Production Timeline</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Production Lead Time
                </Typography>
                <Typography variant="body2">
                  {campaign.timeline.productionPeriod?.leadTimeDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Shipping Time
                </Typography>
                <Typography variant="body2">
                  {campaign.timeline.delivery?.shippingDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Production Start
                </Typography>
                <Typography variant="body2">
                  {formatDate(campaign.timeline.productionPeriod?.startDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estimated Delivery
                </Typography>
                <Typography variant="body2">
                  {formatDate(campaign.timeline.delivery?.estimatedDeliveryDate)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CampaignDetailPage;
